import React, { useEffect, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import { get, put, remove } from "../../redux/ApiHelper";
import { errorToast, successToast } from "../../utils/toast.util";
import { SubjectInterface } from "../../interfaces/subjects.interface";

const SubjectsSetting = () => {
  const [subjects, setSubjects] = useState<string[]>([]);
  const [generalAllSubjects, setGeneralAllSubjects] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [description, setDescription] = useState("");

  const [searchTerm, setSearchTerm] = useState<string>(""); // for searching subjects
  const [loading, setLoading] = useState(false);

  // Fetch subjects on mount
  useEffect(() => {
    const getItems = async () => {
      const res: SubjectInterface = await get("/admin/subject/get-all");
      setSubjects(res.subjects || []);
      setGeneralAllSubjects(res.generalAllSubjests || []);
      setDescription(res.allSubjectsDescription);
    };
    getItems();
  }, []);

  /** Add new subject to database */
  const addItem = async () => {
    const trimmed = inputValue.trim();
    if (trimmed && !subjects.includes(trimmed)) {
      setLoading(true);
      setSubjects((prev) => [...prev, trimmed]);
      setInputValue("");
      try {
        const res = await put("/admin/subject/add", {
          subject: trimmed,
          type: "subjects",
        });
        successToast(res.message);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const addGeneralAllSubject = async (subject) => {
    try {
      const res = await put("/admin/subject/add", {
        subject,
        type: "all-subjects",
      });
      successToast(res.message);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const removeAGeneralAllSubject = async (index: number) => {
    try {
      const res = await remove(`/admin/subject/delete/${index}/all-subjects`);
      successToast(res.message);
    } catch (error) {
      console.log(error);
    }
  };

  /** Handle Enter or comma key */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addItem();
    }
  };

  /** Remove subject from main subjects list */
  const removeItem = async (index: number) => {
    setSubjects((prev) => prev.filter((_, i) => i !== index));
    try {
      const res = await remove(`/admin/subject/delete/${index}/subjects`);
      successToast(res.message);
    } catch (error) {
      console.log(error);
    }
  };

  /** Add subject to generalAllSubjects */
  const addToGeneralSubjects = (subject: string) => {
    if (!generalAllSubjects.includes(subject)) {
      addGeneralAllSubject(subject);
      setGeneralAllSubjects((prev) => [...prev, subject]);
    } else {
      errorToast("Subject already added to all subject");
    }
  };

  /** Remove subject from generalAllSubjects */
  const removeFromGeneralSubjects = (subject: string, index: number) => {
    setGeneralAllSubjects((prev) => prev.filter((item) => item !== subject));
    removeAGeneralAllSubject(index);
  };

  /** Filtered subjects based on search */
  const filteredSubjects = subjects.filter((subject) =>
    subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="w-full max-w-lg mx-auto space-y-6 mt-5">
      <label className="block text-yellow-500 font-medium text-2xl">
        Add a subject to the subject database
      </label>

      {/* Subjects Add Section */}
      <div className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 rounded-md bg-white shadow-sm min-h-[3rem]">
        {subjects.map((item, index) => (
          <span
            key={index}
            className="flex items-center bg-yellow-100 text-black px-3 py-1 rounded-full text-sm font-medium shadow-sm"
          >
            {item}
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="ml-2 bg-red-400 hover:bg-red-500 transition rounded-full p-0.5"
            >
              <RxCross2 />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type and press Enter to save"
          className="flex-1 min-w-[8rem] px-2 py-1 text-sm focus:outline-none"
        />
      </div>

      <div className="text-sm text-gray-600">
        Total Items: {subjects.length}
      </div>

      {/* Search and Add to General Subjects */}
      <div className="space-y-4">
        <label className="block text-green-600 font-medium text-xl">
          Search subject from subject database & Add to All Subjects set
        </label>
        <input
          type="text"
          placeholder="Search subjects and add..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
        />

        {/* Filtered Subject List */}
        <div className="space-y-2">
          {searchTerm && filteredSubjects.length > 0 ? (
            filteredSubjects.map((subject, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 border rounded-md bg-gray-50"
              >
                <span>{subject}</span>
                <button
                  type="button"
                  onClick={() => addToGeneralSubjects(subject)}
                  className="px-2 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Add
                </button>
              </div>
            ))
          ) : searchTerm ? (
            <div className="text-gray-500 text-sm">No subjects found</div>
          ) : null}
        </div>
      </div>

      {/* General Subjects Section */}
      <div className="space-y-4">
        <label className="block text-purple-600 font-medium text-xl">
          ALL Subjects set includes
        </label>
        <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-md bg-white min-h-[3rem]">
          {generalAllSubjects.length > 0 ? (
            generalAllSubjects.map((subject, index) => (
              <span
                key={index}
                className="flex items-center bg-purple-100 text-black px-3 py-1 rounded-full text-sm font-medium shadow-sm"
              >
                {subject}
                <button
                  type="button"
                  onClick={() => removeFromGeneralSubjects(subject, index)}
                  className="ml-2 bg-red-400 hover:bg-red-500 transition rounded-full p-0.5"
                >
                  <RxCross2 />
                </button>
              </span>
            ))
          ) : (
            <span className="text-gray-500 text-sm">No subjects added</span>
          )}
        </div>
        <div className="text-gray-500 text-sm">
          Total all subjects: {generalAllSubjects.length}
        </div>
        <div className="space-y-4">
          <label className="block text-purple-600 font-medium text-xl">
            Set description for all subjects
          </label>

          <div className="flex items-center gap-2">
            {/* Description Input */}
            <textarea
              placeholder="Enter description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
            />

            {/* Save Button */}
            <button
              type="button"
              onClick={async () => {
                if (!description.trim()) {
                  successToast("Please enter a description before saving");
                  return;
                }

                try {
                  setLoading(true);
                  const res = await put("/admin/subject/change-description", {
                    description,
                  });
                  successToast(
                    res.message || "Description saved successfully!"
                  );
                } catch (error) {
                  console.error(error);
                } finally {
                  setLoading(false);
                }
              }}
              className={`px-4 py-2 rounded-md text-white text-sm transition ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-500 hover:bg-green-600"
              }`}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubjectsSetting;
